import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, FormattedNumber} from "react-intl";

import {styled} from "@mui/material/styles";

import InfoTooltipIcon from "components/CustomTooltip/InfoTooltipIcon";
import TruncationTooltip from "components/CustomTooltip/TruncationTooltip";
import ListingCtrBadge from "./ListingCtrBadge";
import ListingPriceCell from "./ListingPriceCell";
import ListingProductDetails from "./ListingProductDetails";
import {OriginBadge} from "./ListingArticleCell";
import {ListingStatusPills, ListingStockBadges} from "./ListingStockCell";
import {black500, greyishBlue400, greyishBlue600} from "assets/jss/main";

ListingMobileArticleCell.propTypes = {
    name: PropTypes.string,
    isEikow: PropTypes.bool,
    brandName: PropTypes.string,
    sku: PropTypes.node,
    // status pills, same contract as ListingStockCell: omitted = hidden
    active: PropTypes.bool,
    validated: PropTypes.bool,
    validationErrors: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.arrayOf(PropTypes.shape({
            path: PropTypes.string,
            message: PropTypes.string,
        })),
    ]),
    hasPushError: PropTypes.bool,
    // "CTR : X%" pill on the right of the status pills, hidden when null
    ctr: PropTypes.number,
    // same contract as ListingPriceCell
    price: PropTypes.number,
    previousPrice: PropTypes.number,
    currency: PropTypes.string,
    priceCompetitiveness: PropTypes.number,
    // N/A when null, like ListingSalesCell
    totalSales: PropTypes.number,
    // same contract as ListingStockCell
    stocks: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        stock: PropTypes.number,
    })),
};

// The single flexible column of the mobile row: every desktop column but the image
// stacks in here, so it takes whatever width the cell got and truncates inside.
const Container = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    minWidth: 0
});

const TagsRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    width: '100%'
});

const Name = styled('div')({
    color: black500,
    fontSize: '14px',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

const SubtitleContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px',
    minWidth: 0
});

const BrandName = styled('span')({
    color: greyishBlue600,
    fontSize: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

const SalesRow = styled('div')({
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
    '& svg': {
        alignSelf: 'center'
    }
});

const SalesValue = styled('span')({
    color: black500,
    fontSize: '14px',
    fontWeight: 600,
    whiteSpace: 'nowrap'
});

const SalesLabel = styled('span')({
    color: greyishBlue400,
    fontSize: '12px',
    whiteSpace: 'nowrap'
});

function ListingMobileArticleCell({
    name,
    isEikow = false,
    brandName,
    sku,
    active,
    validated,
    validationErrors,
    hasPushError,
    ctr,
    price,
    previousPrice,
    currency,
    priceCompetitiveness,
    totalSales,
    stocks = [],
}) {
    const hasPills = undefined !== active || undefined !== validated || true === hasPushError;
    const hasCtr = null !== ctr && undefined !== ctr;

    return (
        <Container>
            {(hasPills || hasCtr) && (
                <TagsRow>
                    <ListingStatusPills
                        active={active}
                        validated={validated}
                        validationErrors={validationErrors}
                        hasPushError={hasPushError}
                    />
                    <ListingCtrBadge ctr={ctr}/>
                </TagsRow>
            )}
            <div>
                <TruncationTooltip title={name ?? ''} placement="top">
                    <Name>{name}</Name>
                </TruncationTooltip>
                <SubtitleContainer>
                    <OriginBadge isEikow={isEikow}/>
                    {brandName && <BrandName>{brandName}</BrandName>}
                </SubtitleContainer>
            </div>
            <ListingProductDetails
                details={[
                    {label: <FormattedMessage id={"retailer.product.list.label.sku"}/>, value: sku},
                ]}
            />
            <ListingPriceCell
                inline
                price={price}
                previousPrice={previousPrice}
                currency={currency}
                priceCompetitiveness={priceCompetitiveness}
            />
            <SalesRow>
                <SalesValue>
                    {null === totalSales || undefined === totalSales ? 'N/A' : <FormattedNumber value={totalSales}/>}
                </SalesValue>
                <SalesLabel>
                    <FormattedMessage id={"retailer.product.list.sales.total"}/>
                </SalesLabel>
                <InfoTooltipIcon titleId={"retailer.product.list.sales.total.tooltip"}/>
            </SalesRow>
            <ListingStockBadges stocks={stocks} collapsedLinesCount={2}/>
        </Container>
    );
}

export default ListingMobileArticleCell;
